import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronsUpDown, Clock3, Handshake, History, House, Languages, LogOut, Moon, Network, PanelLeftClose, PanelLeftOpen, Search, Settings2, Sun,
  UserRound,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router';
import { keys, useMe, useTree } from '../api/hooks.ts';
import { Empty, Spinner } from '../components/ui/index.tsx';
import { ROLE_LABEL } from '../lib/format.ts';
import { useLogout } from '../features/auth/logout.ts';
import { browserLocale, LOCALES, setLocalePref, t, useLocale, type LocalePref } from '../i18n/index.ts';
import { msg } from '../i18n/labels.ts';
import { BrandMark } from './Brand.tsx';
import { useNavCollapsed } from './navRail.ts';
import { TreeProvider, useTreeCtx } from './TreeContext.tsx';

type Theme = 'light' | 'dark' | 'system';
export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem('theme') as Theme) || 'system';
    } catch {
      return 'system';
    }
  });
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('theme', theme);
    } catch {
      /* almacenamiento no disponible */
    }
  }, [theme]);
  return [theme, setTheme];
}

const NAV: Array<{ to: string; label: string; icon: typeof House; end?: boolean; admin?: boolean }> = [
  { to: '', label: msg('Inicio'), icon: House, end: true },
  { to: '/arbol', label: msg('Árbol'), icon: Network },
  { to: '/buscar', label: msg('Buscar'), icon: Search },
  { to: '/cronologia', label: msg('Cronología'), icon: Clock3 },
  { to: '/encuentro', label: msg('Encuentro'), icon: Handshake },
  { to: '/historial', label: msg('Historial'), icon: History },
  { to: '/admin', label: msg('Administrar'), icon: Settings2, admin: true },
];

/**
 * Idioma de la interfaz. "Idioma del navegador" sigue la configuración del navegador; elegir uno lo fija en este
 * navegador. La lista nativa es accesible con teclado y lector de pantalla sin trabajo extra.
 */
export function LanguageSelect({ className, iconClass, compact }: { className?: string; iconClass?: string; compact?: boolean }) {
  const { pref, locale } = useLocale();
  const auto = LOCALES.find((l) => l.code === browserLocale())!.name;
  return (
    <label
      title={compact ? `${t('Idioma')}: ${LOCALES.find((l) => l.code === locale)!.name}` : undefined}
      className={clsx('relative flex cursor-pointer items-center gap-3 focus-within:outline-2 focus-within:outline-pine', className)}
    >
      <Languages className={iconClass} aria-hidden />
      <span className="sr-only">{t('Idioma')}</span>
      {/* Contraído, la lista queda invisible sobre el ícono: se abre igual con clic, toque o el control remoto. */}
      <select
        value={pref}
        onChange={(e) => void setLocalePref(e.target.value as LocalePref)}
        className={clsx(
          'cursor-pointer appearance-none bg-transparent outline-none',
          compact ? 'absolute inset-0 opacity-0' : 'min-w-0 flex-1 truncate pr-5',
        )}
      >
        <option value="auto">{t('Idioma del navegador ({name})', { name: auto })}</option>
        {LOCALES.map((l) => (
          <option key={l.code} value={l.code} lang={l.code}>{l.name}</option>
        ))}
      </select>
      {!compact && <ChevronsUpDown className="pointer-events-none absolute right-3 size-3.5 text-ink-faint" aria-hidden />}
    </label>
  );
}

function TreeSwitcher() {
  const { tree } = useTreeCtx();
  const me = useMe().data;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left hover:bg-surface-2"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-serif text-[15px] font-semibold">{tree.name}</span>
          <span className="block text-[12.5px] text-ink-faint">{ROLE_LABEL[tree.role]} · {t('{count} personas', { count: tree.personsCount })}</span>
        </span>
        <ChevronsUpDown className="size-4 text-ink-faint" />
      </button>
      {open && (
        <div className="absolute inset-x-0 top-full z-30 mt-1 rounded-2xl border border-line bg-surface p-1.5 shadow-soft" onMouseLeave={() => setOpen(false)}>
          {me?.trees.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setOpen(false);
                navigate(`/t/${t.id}`);
              }}
              className={clsx('block w-full truncate rounded-lg px-3 py-2 text-left text-[14.5px] hover:bg-surface-2', t.id === tree.id && 'font-semibold')}
            >
              {t.name}
            </button>
          ))}
          <Link to="/arboles" className="mt-1 block rounded-lg border-t border-line px-3 py-2 text-[14px] text-ink-soft hover:bg-surface-2" onClick={() => setOpen(false)}>
            {t('Todos mis árboles')}
          </Link>
        </div>
      )}
    </div>
  );
}

/** Fila del menú lateral: contraído muestra solo el ícono, con el nombre como ayuda emergente y para lectores de pantalla. */
const railItem = (collapsed: boolean) =>
  clsx(
    'flex items-center gap-3 rounded-xl text-[14.5px] text-ink-soft hover:bg-surface-2',
    collapsed ? 'justify-center px-0 py-2.5 short:py-1.5' : 'px-3 py-2 short:py-1.5',
  );

function SideNav() {
  const { path, isAdmin } = useTreeCtx();
  const me = useMe().data;
  const [theme, setTheme] = useTheme();
  const [collapsed, toggle] = useNavCollapsed();
  const logout = useLogout();
  const tip = (label: string) => (collapsed ? label : undefined);
  const label = (children: ReactNode) => <span className={collapsed ? 'sr-only' : 'truncate'}>{children}</span>;
  const themeLabel = theme === 'dark' ? t('Modo claro') : t('Modo oscuro');
  const toggleLabel = collapsed ? t('Expandir menú') : t('Contraer menú');
  return (
    <aside
      className={clsx(
        'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-line bg-bg pb-3 pt-5 short:pt-3 md:flex',
        collapsed ? 'w-[68px] px-2' : 'w-[248px] px-3',
      )}
    >
      <div className={clsx('mb-5 flex items-center short:mb-3', collapsed ? 'flex-col gap-3' : 'gap-2')}>
        <Link to="/arboles" title={tip(t('Mis árboles'))} className={clsx('flex min-w-0 items-center gap-2.5', !collapsed && 'flex-1 px-2.5')}>
          <BrandMark size={30} />
          {label(<span className="font-serif text-[18px] font-semibold">Gallarday</span>)}
        </Link>
        <button
          onClick={toggle}
          aria-expanded={!collapsed}
          aria-label={toggleLabel}
          title={toggleLabel}
          className="grid size-9 shrink-0 place-items-center rounded-xl text-ink-faint hover:bg-surface-2 hover:text-ink"
        >
          {collapsed ? <PanelLeftOpen className="size-[18px]" aria-hidden /> : <PanelLeftClose className="size-[18px]" aria-hidden />}
        </button>
      </div>
      {!collapsed && <TreeSwitcher />}
      {/* En pantallas bajas (televisores) el menú se desplaza para que todas las opciones sigan al alcance. */}
      <div className="-mx-1 flex min-h-0 flex-1 flex-col overflow-y-auto px-1 [scrollbar-width:none]">
        <nav aria-label={t('Secciones del árbol')} className={clsx('flex flex-col gap-0.5', !collapsed && 'mt-4 short:mt-2')}>
          {NAV.filter((n) => !n.admin || isAdmin).map((n) => (
            <NavLink
              key={n.label}
              to={path(n.to)}
              end={n.end}
              title={tip(t(n.label))}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-3 rounded-xl text-[15px] transition-colors',
                  collapsed ? 'justify-center py-2.5 short:py-2' : 'px-3 py-2.5 short:py-1.5',
                  isActive ? 'bg-pine font-medium text-on-pine' : 'text-ink-soft hover:bg-surface-2 hover:text-ink',
                )
              }
            >
              <n.icon className="size-[18px] shrink-0" aria-hidden />
              {label(t(n.label))}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto flex flex-col gap-0.5 border-t border-line pt-3 short:pt-2">
          <Link to="/cuenta" title={tip(me?.user.displayName ?? t('Mi cuenta'))} className={railItem(collapsed)}>
            <UserRound className="size-[18px] shrink-0" aria-hidden />
            {label(me?.user.displayName ?? t('Mi cuenta'))}
          </Link>
          <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} title={tip(themeLabel)} className={clsx(railItem(collapsed), 'text-left')}>
            {theme === 'dark' ? <Sun className="size-[18px] shrink-0" aria-hidden /> : <Moon className="size-[18px] shrink-0" aria-hidden />}
            {label(themeLabel)}
          </button>
          <LanguageSelect compact={collapsed} className={railItem(collapsed)} iconClass="size-[18px] shrink-0" />
          <button onClick={logout} title={tip(t('Cerrar sesión'))} className={clsx(railItem(collapsed), 'text-left')}>
            <LogOut className="size-[18px] shrink-0" aria-hidden />
            {label(t('Cerrar sesión'))}
          </button>
        </div>
      </div>
    </aside>
  );
}

function BottomNav() {
  const { path } = useTreeCtx();
  const items = [NAV[0]!, NAV[1]!, NAV[2]!, NAV[4]!];
  return (
    <nav aria-label={t('Secciones')} className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
      <div className="grid grid-cols-5">
        {items.map((n) => (
          <NavLink
            key={n.label}
            to={path(n.to)}
            end={n.end}
            className={({ isActive }) => clsx('flex flex-col items-center gap-0.5 py-2 text-[11.5px]', isActive ? 'font-semibold text-ink' : 'text-ink-faint')}
          >
            <n.icon className="size-5" aria-hidden />
            {t(n.label)}
          </NavLink>
        ))}
        <NavLink to={path('/mas')} className={({ isActive }) => clsx('flex flex-col items-center gap-0.5 py-2 text-[11.5px]', isActive ? 'font-semibold text-ink' : 'text-ink-faint')}>
          <Settings2 className="size-5" aria-hidden />
          {t('Más')}
        </NavLink>
      </div>
    </nav>
  );
}

function MobileTop() {
  const { tree } = useTreeCtx();
  return (
    <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-line bg-bg/90 px-4 py-2.5 backdrop-blur md:hidden">
      <Link to="/arboles" aria-label={t('Mis árboles')}>
        <BrandMark size={28} />
      </Link>
      <span className="truncate font-serif text-[16px] font-semibold">{tree.name}</span>
    </header>
  );
}

/** Página "Más" en móvil: enlaces que no caben en la barra inferior. */
export function MorePage() {
  const { path, isAdmin } = useTreeCtx();
  const [theme, setTheme] = useTheme();
  const links = NAV.filter((n) => !n.admin || isAdmin).slice(3);
  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <h1 className="mb-4 text-2xl">{t('Más opciones')}</h1>
      <div className="flex flex-col divide-y divide-line rounded-2xl border border-line bg-surface">
        {links.map((n) => (
          <Link key={n.label} to={path(n.to)} className="flex items-center gap-3 px-4 py-3.5">
            <n.icon className="size-5 text-ink-soft" aria-hidden /> {t(n.label)}
          </Link>
        ))}
        <Link to="/arboles" className="flex items-center gap-3 px-4 py-3.5">
          <Network className="size-5 text-ink-soft" aria-hidden /> {t('Mis árboles')}
        </Link>
        <Link to="/cuenta" className="flex items-center gap-3 px-4 py-3.5">
          <UserRound className="size-5 text-ink-soft" aria-hidden /> {t('Mi cuenta')}
        </Link>
        <button onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="flex items-center gap-3 px-4 py-3.5 text-left">
          {theme === 'dark' ? <Sun className="size-5 text-ink-soft" /> : <Moon className="size-5 text-ink-soft" />}
          {theme === 'dark' ? t('Modo claro') : t('Modo oscuro')}
        </button>
        <LanguageSelect className="px-4 py-3.5" iconClass="size-5 text-ink-soft" />
      </div>
    </div>
  );
}

/** Layout de todas las páginas dentro de un árbol. */
export function TreeLayout() {
  const { treeId } = useParams();
  const tree = useTree(treeId);
  if (tree.isLoading) return <Spinner label={t('Abriendo el árbol')} />;
  if (tree.error || !tree.data) {
    return (
      <Empty title={t('No encontramos este árbol')} action={<Link className="underline" to="/arboles">{t('Ver mis árboles')}</Link>}>
        {t('Puede que no tengas acceso o que el enlace sea incorrecto.')}
      </Empty>
    );
  }
  return (
    <TreeProvider tree={tree.data}>
      <div className="flex min-h-dvh">
        <SideNav />
        <div className="flex min-w-0 flex-1 flex-col pb-[72px] md:pb-0">
          <MobileTop />
          <main id="contenido" className="min-w-0 flex-1">
            <Outlet />
          </main>
        </div>
        <BottomNav />
      </div>
    </TreeProvider>
  );
}

/** Contenedor estándar de página. */
export function Page({ title, subtitle, actions, children, wide, back }: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  wide?: boolean;
  /** Enlace para volver, sobre el título. */
  back?: ReactNode;
}) {
  return (
    <div className={clsx('mx-auto w-full px-4 pb-16 pt-6 md:px-8 md:pt-10', wide ? 'max-w-6xl' : 'max-w-4xl')}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {back && <div className="mb-2">{back}</div>}
          <h1 className="text-[28px] md:text-[34px]">{title}</h1>
          {subtitle && <p className="mt-1.5 max-w-2xl text-[15.5px] text-ink-soft">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

export function useRefreshMe() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: keys.me });
}
